/**
 * AuthContext.jsx - Authentication context using MSAL
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig, loginRequest } from '../authConfig';

// Check if we're using local auth before initializing MSAL
const useLocalAuth = import.meta.env.DEV && import.meta.env.VITE_LOCAL_AUTH === 'true';

// Only initialize MSAL if not using local authentication
let msalInstance = null;
if (!useLocalAuth) {
  msalInstance = new PublicClientApplication(msalConfig);
  
  // Handle the redirect promise when app loads
  // This is necessary when using loginRedirect (redirects happen when returning from auth)
  if (msalInstance.initialize) {
    msalInstance.initialize().then(() => {
      // Handle redirect promise after the instance is initialized
      msalInstance.handleRedirectPromise()
        .then(response => {
          // If we have a successful response from redirect, process it
          if (response) {
            console.log("Redirect response processed successfully");
          }
        })
        .catch(error => {
          console.error("Redirect error:", error);
        });
    });
  } else {
    // For older versions of MSAL that don't have initialize method
    msalInstance.handleRedirectPromise()
      .then(response => {
        if (response) {
          console.log("Redirect response processed successfully");
        }
      })
      .catch(error => {
        console.error("Redirect error:", error);
      });
  }
}

// Create the auth context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [msalReady, setMsalReady] = useState(false);

  // Helper to clear stale auth attempts
  const clearStaleAuthAttempts = () => {
    const authAttemptTimestamp = sessionStorage.getItem('msal_auth_attempt');
    if (authAttemptTimestamp) {
      const currentTime = Date.now();
      // If the last attempt was more than 30 seconds ago, clear it
      if ((currentTime - parseInt(authAttemptTimestamp)) > 30000) {
        console.log('Clearing stale auth attempt timestamp');
        sessionStorage.removeItem('msal_auth_attempt');
      }
    }
  };

  useEffect(() => {
    // Clear any stale auth attempts on component mount
    clearStaleAuthAttempts();

    const initializeAuth = async () => {
      // Mark MSAL as ready to use
      setMsalReady(true);

      // Skip actual auth in development if local auth flag is set
      if (import.meta.env.DEV && import.meta.env.VITE_LOCAL_AUTH === 'true') {
        console.log('Using local development authentication mode');
        // Create mock account for development
        setAccount({
          name: 'Developer User',
          username: 'dev@example.com',
          localAccountId: 'dev-account-123'
        });
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      // Safety check for msalInstance
      if (!msalInstance) {
        console.error('MSAL instance not initialized');
        setIsLoading(false);
        return;
      }

      try {
        // Handle any active redirect first before checking accounts
        const redirectResponse = await msalInstance.handleRedirectPromise();

        if (redirectResponse) {
          // We got back from a redirect, handle the response
          console.log('Processing redirect response');
          if (redirectResponse.account) {
            msalInstance.setActiveAccount(redirectResponse.account);
            setAccount(redirectResponse.account);
            setIsAuthenticated(true);
          }
          setIsLoading(false);
          return;
        }

        // Check if there are already accounts in the cache
        const accounts = msalInstance.getAllAccounts();
        const activeAccount = msalInstance.getActiveAccount();

        if (activeAccount || accounts.length > 0) {
          // Prefer the account explicitly selected by MSAL. Falling back to the
          // first cached account preserves the existing single-account behavior.
          const cachedAccount = activeAccount || accounts[0];
          if (!activeAccount) {
            msalInstance.setActiveAccount(cachedAccount);
          }
          setAccount(cachedAccount);
          setIsAuthenticated(true);
          setIsLoading(false);
        } else {
          // Check if we've already tried to login in this session
          const authAttemptTimestamp = sessionStorage.getItem('msal_auth_attempt');
          const currentTime = Date.now();

          // If we've tried in the last 3 seconds, don't try again (prevents loops)
          if (authAttemptTimestamp && (currentTime - parseInt(authAttemptTimestamp)) < 3000) {
            console.log('Recent auth attempt detected, preventing redirect loop');
            setIsLoading(false);
            return;
          }

          // No accounts found, trigger login automatically
          try {
            console.log('No accounts found, automatically redirecting to login...');
            // Set timestamp to prevent redirect loops
            sessionStorage.setItem('msal_auth_attempt', currentTime.toString());
            // We use loginRedirect for automatic login to avoid popup blockers
            await msalInstance.loginRedirect(loginRequest);
            // This code will not execute immediately as the page will redirect
          } catch (loginError) {
            console.error('Auto-login failed:', loginError);
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error('Authentication initialization error:', error);
        setIsLoading(false);
      }
    };

    // Handle login redirect success
    const handleResponse = (response) => {
      if (response && response.account) {
        msalInstance.setActiveAccount(response.account);
        setAccount(response.account);
        setIsAuthenticated(true);
      }
      setIsLoading(false);
    };

    // Register redirect callbacks (only if using MSAL)
    if (msalInstance) {
      msalInstance.addEventCallback((event) => {
        if (event.eventType === "loginSuccess" ||
            event.eventType === "acquireTokenSuccess") {
          if (event.payload) {
            handleResponse(event.payload);
          }
        }
      });
    }

    initializeAuth();
  }, []);

  // Login function
  const login = async (forceLogin = false) => {
    if (!msalReady || !msalInstance) {
      console.error("MSAL is not ready yet");
      return;
    }

    // Only call loginRedirect if not already authenticated
    if (!forceLogin && msalInstance.getAllAccounts().length > 0) {
      console.log("User already has an account, no need to login again");
      return;
    }

    // Check for recent auth attempts to prevent loops
    if (!forceLogin) {
      const authAttemptTimestamp = sessionStorage.getItem('msal_auth_attempt');
      const currentTime = Date.now();

      if (authAttemptTimestamp && (currentTime - parseInt(authAttemptTimestamp)) < 3000) {
        console.log('Recent auth attempt detected, preventing redirect loop');
        return;
      }
    } else {
      // If force login, clear any existing auth attempt timestamp
      sessionStorage.removeItem('msal_auth_attempt');
    }

    try {
      console.log("Initiating login redirect");
      // Set timestamp to prevent redirect loops
      sessionStorage.setItem('msal_auth_attempt', Date.now().toString());
      await msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("Login redirect failed:", error);
    }
  };

  // Reset authentication state and try again
  const resetAndLogin = () => {
    // Clear all MSAL-related storage
    sessionStorage.removeItem('msal_auth_attempt');

    // Force a new login attempt
    login(true);
  };

  // Logout function
  const logout = () => {
    if (!msalInstance) {
      console.error('MSAL instance not initialized');
      return;
    }
    localStorage.removeItem('msal.token');
    msalInstance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
    setAccount(null);
    setIsAuthenticated(false);
  };

  // Get access token
  const getAccessToken = useCallback(async () => {
    // For local development with auth bypass
    if (import.meta.env.VITE_LOCAL_AUTH === 'true') {
      return 'dev-mock-token';
    }

    if (!account || !msalReady || !msalInstance) return null;
    try {
      const response = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
      return response.accessToken;
    } catch {
      try {
        const response = await msalInstance.acquireTokenPopup(loginRequest);
        return response.accessToken;
      } catch {
        return null;
      }
    }
  }, [account, msalReady]);

  // Expose token getter to axios interceptor
  useEffect(() => {
    window.__getAccessToken = getAccessToken;
    return () => {
      delete window.__getAccessToken;
    };
  }, [getAccessToken]);

  const value = {
    msalInstance,
    account,
    isAuthenticated,
    isLoading,
    login,
    logout,
    resetAndLogin,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;